import React, { useEffect, useState } from "react";
import {
    Button,
    Typography,
    Box,
    Grid,
    Tooltip,
    Card,
    CardContent,
    IconButton,
    CardActions,
    Divider,
    Skeleton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from '@mui/icons-material/Edit';
import { Add } from "@mui/icons-material";
import { NoData } from "../../../Base/views/NotAuthorized";
import { GetAtsAgreementTemplatesHelper, DeleteAtsAgreementTemplateHelper } from "../../../ATS/helper/AtsAgreementHelper";
import ConfirmationDialog from "../../../Base/views/ConfirmationDialog";

function AgreementTemplates({ setCurrentMode, setData }) {
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [formId, setFormId] = useState("");
    const [deleteConfirm, setDeleteConfirm] = useState(false);

    const fetchTemplates = () => {
        setIsLoading(true);
        GetAtsAgreementTemplatesHelper().then((res) => {
            if (res && !res.hasError) {
                setTemplates(res.data?.data || []);
            }
            setIsLoading(false);
        }).catch(() => {
            setIsLoading(false);
        });
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    const handleEdit = (template) => {
        setCurrentMode("Edit");
        setData(template);
    };

    const handleDeleteConfirm = (id) => {
        setFormId(id);
        setDeleteConfirm(!deleteConfirm);
    };

    const handleDelete = () => {
        DeleteAtsAgreementTemplateHelper(formId).then((res) => {
            if (res && !res.hasError) {
                fetchTemplates();
            }
        });
        setFormId("");
        setDeleteConfirm(!deleteConfirm);
    };

    const handleAdd = () => {
        setCurrentMode("Add");
        setData("");
    };

    return (
        <Box sx={{ pb: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Agreement Templates
                </Typography>
                <Button
                    onClick={() => handleAdd()}
                    variant="contained"
                    className="mpl-primary-btn"
                    startIcon={<Add />}
                >
                    Add Agreement Template
                </Button>
            </Box>
            <>
                {isLoading ? (
                    <Grid container direction="row" alignItems="center" spacing={2}>
                        {Array(4)
                            .fill()
                            .map((_, index) => (
                                <Grid key={index} item lg={3} md={3} sm={3} xs={12}>
                                    <Card
                                        sx={{
                                            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                            minHeight: "125px",
                                            maxHeight: "125px"
                                        }}
                                    >
                                        <CardContent>
                                            <Skeleton sx={{ height: 30 }} animation="wave" variant="rectangular" />
                                        </CardContent>
                                        <Divider variant="middle" />
                                        <CardActions sx={{ padding: "0px" }}>
                                            <Box sx={{ padding: "0px 20px 8px 20px", display: "flex", justifyContent: "space-between", width: "100%" }}>
                                                <Skeleton animation="wave" sx={{ borderRadius: 4 }} height={20} width="30%" />
                                                <Skeleton animation="wave" sx={{ borderRadius: 4 }} height={20} width="30%" />
                                            </Box>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                    </Grid>
                ) : (
                    templates.length > 0 ? (
                        <Grid container direction="row" alignItems="center" spacing={2}>
                            {templates.map((template, index) => (
                                <Grid key={index} item lg={3} md={3} sm={3} xs={12}>
                                    <Card sx={{ ":hover": { cursor: "pointer" }, minHeight: "125px", maxHeight: "125px" }}>
                                        <CardContent>
                                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                <Tooltip title={template.agreement_type}>
                                                    <Typography variant="h5" className="truncate-heading">{template.agreement_type}</Typography>
                                                </Tooltip>
                                            </Box>
                                        </CardContent>
                                        <Divider variant="middle" />
                                        <CardActions sx={{ padding: "0px" }}>
                                            <Box
                                                sx={{
                                                    padding: "0px 20px 8px 20px",
                                                    display: "flex",
                                                    WebkitBoxPack: "justify",
                                                    justifyContent: "space-between",
                                                    width: "100%",
                                                }}
                                            >
                                                <Tooltip title="Edit">
                                                    <IconButton onClick={() => handleEdit(template)}>
                                                        <EditIcon color="primary" fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {
                                                    !template.in_use ? (
                                                        <Tooltip title="Delete">
                                                            <IconButton onClick={() => handleDeleteConfirm(template.id)}>
                                                                <DeleteIcon color="error" fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title="Agreements have been generated using this template. Please create a new template.">
                                                            <IconButton sx={{ color: 'grey.500' }}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )
                                                }
                                            </Box>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <NoData title="You haven’t created any agreement templates yet" />
                    )
                )}
            </>
            <ConfirmationDialog
                title="Delete Template?"
                content="Are you sure want to Delete this Template?"
                openDialog={deleteConfirm}
                closeDialog={setDeleteConfirm}
                OkButtonText="OK"
                onSubmit={handleDelete}
            />
        </Box>
    );
}

export default AgreementTemplates;
